import { FormEvent, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  MultipleChoiceSurveyQuestion,
  RatingSurveyQuestion,
  SatisfactionSurveyData,
  SurveyAnswers,
  SurveyQuestion,
  SurveyRule,
  SurveyTranslation,
  getSatisfactionSurvey,
  submitSatisfactionSurvey,
} from '../../services/satisfactionSurveyService';
import NavBar from '../Common/NavBar';

type AnswerValue = string | string[] | number | undefined;
type Conclusion = 'positive' | 'neutral' | 'negative' | '';
type SurveyLanguage = 'pt' | 'en';

const GOOGLE_REVIEW_URL = 'https://g.co/kgs/uNLvMjE';

interface QuestionProps {
  question: SurveyQuestion;
  language: SurveyLanguage;
  translation?: SurveyTranslation;
  value: AnswerValue;
  otherValue: string;
  onChange: (value: string | string[] | number) => void;
  onOtherChange: (value: string) => void;
}

interface RatingQuestionProps {
  question: RatingSurveyQuestion;
  value: AnswerValue;
  onChange: (value: number) => void;
}

interface MultipleChoiceQuestionProps {
  question: MultipleChoiceSurveyQuestion;
  language: SurveyLanguage;
  translation?: SurveyTranslation;
  value: AnswerValue;
  otherValue: string;
  onChange: (value: string | string[]) => void;
  onOtherChange: (value: string) => void;
}

interface TextQuestionProps {
  value: AnswerValue;
  onChange: (value: string) => void;
}

const checkRule = (rule: SurveyRule | undefined, answers: SurveyAnswers) => {
  if (!rule) return true;

  const value = answers[rule.questionId];
  if (value === undefined || value === null || value === '') return false;

  if (rule.operator === 'lte')
    return typeof value === 'number' && value <= rule.value;
  if (rule.operator === 'gte')
    return typeof value === 'number' && value >= rule.value;
  if (rule.operator === 'eq') return value === rule.value;
  if (rule.operator === 'between')
    return typeof value === 'number' && value >= rule.min && value <= rule.max;
  if (rule.operator === 'answered')
    return (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      (!Array.isArray(value) || value.length > 0)
    );

  return true;
};

const getVisibleQuestions = (
  questions: SurveyQuestion[],
  answers: SurveyAnswers
) => {
  const visibleQuestionIds = new Set<string>();

  return questions.filter((question) => {
    if (!question.showWhen) {
      visibleQuestionIds.add(question.id);
      return true;
    }

    const isVisible =
      visibleQuestionIds.has(question.showWhen.questionId) &&
      checkRule(question.showWhen, answers);

    if (isVisible) {
      visibleQuestionIds.add(question.id);
    }

    return isVisible;
  });
};

const isAnswerFilled = (value: AnswerValue) =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  (!Array.isArray(value) || value.length > 0);

const getProgressiveQuestions = (
  questions: SurveyQuestion[],
  answers: SurveyAnswers
) => {
  const firstMissingIndex = questions.findIndex(
    (question) => !isAnswerFilled(answers[question.id])
  );

  if (firstMissingIndex === -1) return questions;

  return questions.slice(0, firstMissingIndex + 1);
};

const isOtherSelected = (value: AnswerValue) =>
  (Array.isArray(value) && value.includes('other')) || value === 'other';

const getQuestionLabel = (
  question: SurveyQuestion,
  translation?: SurveyTranslation
) => {
  return translation?.questions?.[question.id]?.label ?? question.label;
};

const getOptionLabel = (
  question: MultipleChoiceSurveyQuestion,
  optionId: string,
  label: string,
  translation?: SurveyTranslation
) => {
  return translation?.questions?.[question.id]?.options?.[optionId] ?? label;
};

const pruneAnswers = (
  questions: SurveyQuestion[],
  currentAnswers: SurveyAnswers
) => {
  const visibleQuestions = getProgressiveQuestions(
    getVisibleQuestions(questions, currentAnswers),
    currentAnswers
  );

  return visibleQuestions.reduce<SurveyAnswers>((acc, question) => {
    const value = currentAnswers[question.id];

    if (value !== undefined) {
      acc[question.id] = value;
    }

    if (question.type === 'multiple_choice' && isOtherSelected(value)) {
      acc[`${question.id}_other`] =
        currentAnswers[`${question.id}_other`] ?? '';
    }

    return acc;
  }, {});
};

const getRatingButtonClassName = (rating: number, selected: boolean) => {
  if (rating >= 9) {
    return selected
      ? 'border-green-600 bg-green-600 text-white'
      : 'border-green-200 bg-green-50 text-green-800';
  }

  if (rating >= 7) {
    return selected
      ? 'border-yellow-500 bg-yellow-500 text-white'
      : 'border-yellow-200 bg-yellow-50 text-yellow-800';
  }

  return selected
    ? 'border-red-500 bg-red-500 text-white'
    : 'border-red-200 bg-red-50 text-red-800';
};

const RatingQuestion = ({ question, value, onChange }: RatingQuestionProps) => {
  const values = Array.from(
    { length: question.max - question.min + 1 },
    (_, index) => question.min + index
  );

  return (
    <div className='grid grid-cols-5 sm:grid-cols-10 gap-2'>
      {values.map((rating) => (
        <button
          key={rating}
          type='button'
          onClick={() => onChange(rating)}
          className={`h-11 rounded border text-sm font-semibold ${getRatingButtonClassName(
            rating,
            value === rating
          )}`}
        >
          {rating}
        </button>
      ))}
    </div>
  );
};

const MultipleChoiceQuestion = ({
  question,
  language,
  translation,
  value,
  otherValue,
  onChange,
  onOtherChange,
}: MultipleChoiceQuestionProps) => {
  const selectedValues = Array.isArray(value) ? value : [];
  const hasOther = question.allowMultiple
    ? selectedValues.includes('other')
    : value === 'other';

  const setMultipleChoice = (optionId: string) => {
    if (selectedValues.includes(optionId)) {
      onChange(selectedValues.filter((id) => id !== optionId));
      return;
    }

    onChange([...selectedValues, optionId]);
  };

  return (
    <div className='space-y-2'>
      {question.options.map((option) => {
        const checked = question.allowMultiple
          ? selectedValues.includes(option.id)
          : value === option.id;

        return (
          <div key={option.id}>
            <label
              className={`flex items-center gap-3 rounded border px-4 py-3 ${
                checked ? 'border-[#004775] bg-blue-50' : 'border-gray-200'
              }`}
            >
              <input
                type={question.allowMultiple ? 'checkbox' : 'radio'}
                name={question.id}
                checked={checked}
                onChange={() =>
                  question.allowMultiple
                    ? setMultipleChoice(option.id)
                    : onChange(option.id)
                }
              />

              <span className='text-sm text-gray-800'>
                {getOptionLabel(question, option.id, option.label, translation)}
              </span>
            </label>

            {option.allowsText && hasOther && checked && (
              <input
                className='mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#004775] focus:outline-none focus:ring-1 focus:ring-[#004775]'
                value={otherValue}
                onChange={(event) => onOtherChange(event.target.value)}
                placeholder={
                  language === 'pt' ? 'Especifique' : 'Please specify'
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const TextQuestion = ({ value, onChange }: TextQuestionProps) => (
  <textarea
    className='w-full min-h-[120px] rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#004775] focus:outline-none focus:ring-1 focus:ring-[#004775]'
    value={value ?? ''}
    onChange={(event) => onChange(event.target.value)}
  />
);

const QuestionField = ({
  question,
  language,
  translation,
  value,
  otherValue,
  onChange,
  onOtherChange,
}: QuestionProps) => {
  if (question.type === 'rating') {
    return (
      <RatingQuestion question={question} value={value} onChange={onChange} />
    );
  }

  if (question.type === 'multiple_choice') {
    return (
      <MultipleChoiceQuestion
        question={question}
        language={language}
        translation={translation}
        value={value}
        otherValue={otherValue}
        onChange={onChange}
        onOtherChange={onOtherChange}
      />
    );
  }

  return <TextQuestion value={value} onChange={onChange} />;
};

const SatisfactionSurveyPage = () => {
  const [data, setData] = useState<SatisfactionSurveyData | null>(null);
  const [language, setLanguage] = useState<SurveyLanguage>('pt');
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [conclusion, setConclusion] = useState<Conclusion>('');

  const { token } = useParams();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      if (!token) {
        setError('Questionário não encontrado.');
        setIsLoading(false);
        return;
      }

      try {
        const result = await getSatisfactionSurvey(token);
        setData(result);
        setSubmitted(result.status === 'submitted');
        setConclusion(result.conclusion ?? '');
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Erro ao obter questionário'
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [token]);

  const visibleQuestions = useMemo(() => {
    const questions = getVisibleQuestions(
      data?.survey?.questions || [],
      answers
    );
    return getProgressiveQuestions(questions, answers);
  }, [answers, data]);

  const translation =
    language === 'pt' ? undefined : data?.survey.translations?.[language];

  const texts = useMemo(
    () => ({
      title:
        language === 'pt'
          ? 'Inquérito de satisfação'
          : 'Client satisfaction survey',
      loading: language === 'pt' ? 'A carregar...' : 'Loading...',
      submit: language === 'pt' ? 'Submeter' : 'Submit',
      submitting: language === 'pt' ? 'A submeter...' : 'Submitting...',
      submitted:
        language === 'pt'
          ? 'Obrigado. As suas respostas foram submetidas.'
          : 'Thank you. Your answers have been submitted.',
      missingAnswers:
        language === 'pt'
          ? 'Por favor responda a todas as perguntas.'
          : 'Please answer all questions.',
      notFound:
        language === 'pt'
          ? 'Questionário não encontrado.'
          : 'Survey not found.',
      fetchError:
        language === 'pt'
          ? 'Erro ao obter questionário'
          : 'Error loading survey',
      submitError:
        language === 'pt'
          ? 'Erro ao submeter questionário'
          : 'Error submitting survey',
      introOpinion:
        language === 'pt'
          ? 'Gostaríamos de conhecer a sua opinião sobre a experiência que teve connosco e com o gestor responsável pelo seu acompanhamento.'
          : 'We would like to know your opinion about your experience with us and with the manager responsible for supporting you.',
      introDuration:
        language === 'pt'
          ? 'Este questionário demora menos de 2 minutos.'
          : 'This survey takes less than 2 minutes.',
    }),
    [language]
  );

  const setAnswer = (questionId: string, value: string | string[] | number) => {
    setAnswers((current) =>
      pruneAnswers(data?.survey?.questions || [], {
        ...current,
        [questionId]: value,
      })
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      toast.error(texts.notFound);
      return;
    }

    const missingQuestion = visibleQuestions.find((question) => {
      const value = answers[question.id];
      return question.required && !isAnswerFilled(value);
    });

    if (missingQuestion) {
      toast.error(texts.missingAnswers);
      return;
    }

    const visibleAnswers = visibleQuestions.reduce<SurveyAnswers>(
      (acc, question) => {
        const value = answers[question.id];
        if (value !== undefined) {
          acc[question.id] = value;
        }

        if (question.type === 'multiple_choice' && isOtherSelected(value)) {
          acc[`${question.id}_other`] = answers[`${question.id}_other`] ?? '';
        }

        return acc;
      },
      {}
    );

    setIsSubmitting(true);
    try {
      const result = await submitSatisfactionSurvey(
        token,
        visibleAnswers,
        searchParams.get('source')
      );
      setConclusion(result.conclusion ?? '');
      setSubmitted(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : texts.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='min-h-screen bg-[#F3F4F6]'>
      <NavBar />

      <main className='mx-auto max-w-3xl px-4 py-8 sm:px-6'>
        <section className='rounded bg-white p-6 shadow-sm'>
          <div className='flex flex-wrap items-start justify-between gap-4'>
            <h1 className='text-2xl font-semibold text-gray-900'>
              {texts.title}
            </h1>

            <div className='inline-flex rounded border border-gray-200 p-1'>
              {(['pt', 'en'] as SurveyLanguage[]).map((item) => (
                <button
                  key={item}
                  type='button'
                  onClick={() => setLanguage(item)}
                  className={`rounded px-3 py-1 text-xs font-semibold ${
                    language === item
                      ? 'bg-[#004775] text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {item.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {!isLoading && !error && !submitted && (
            <div className='mt-6 space-y-5 text-center text-gray-900'>
              <p className='text-lg sm:text-xl'>
                {language === 'pt' ? (
                  <>
                    Obrigado por ter confiado na{' '}
                    <strong>AMCO Intermediários de Crédito.</strong>
                  </>
                ) : (
                  <>
                    Thank you for trusting{' '}
                    <strong>AMCO Intermediários de Crédito.</strong>
                  </>
                )}
              </p>

              <p className='mx-auto max-w-2xl text-base sm:text-lg'>
                {texts.introOpinion}
              </p>

              <p className='rounded bg-[#005C9F] px-4 py-3 text-base font-semibold text-white sm:text-lg'>
                {texts.introDuration}
              </p>
            </div>
          )}

          {isLoading && <p className='mt-6 text-gray-600'>{texts.loading}</p>}

          {!isLoading && error && (
            <p className='mt-6 text-sm font-medium text-red-600'>{error}</p>
          )}

          {!isLoading && !error && submitted && (
            <div className='mt-6 space-y-4 text-gray-700'>
              {conclusion &&
                (translation?.conclusions?.[conclusion]?.title ||
                  data?.survey.conclusions[conclusion]?.title) && (
                  <p className='text-lg font-semibold text-gray-900'>
                    {translation?.conclusions?.[conclusion]?.title ||
                      data?.survey.conclusions[conclusion]?.title}
                  </p>
                )}

              {conclusion === 'positive' && (
                <p>
                  <strong>Link:</strong>{' '}
                  <a
                    href={GOOGLE_REVIEW_URL}
                    target='_blank'
                    rel='noreferrer'
                    className='font-semibold text-[#004775] underline'
                  >
                    {language === 'pt' ? 'Avaliação Google' : 'Google Rating'}
                  </a>
                </p>
              )}

              <p className='whitespace-pre-line'>
                {conclusion
                  ? translation?.conclusions?.[conclusion]?.message ||
                    data?.survey.conclusions[conclusion]?.message
                  : texts.submitted}
              </p>
            </div>
          )}

          {!isLoading && !error && !submitted && (
            <form onSubmit={handleSubmit} className='mt-6 space-y-6'>
              {visibleQuestions.map((question) => (
                <div key={question.id}>
                  <label className='mb-3 block text-sm font-medium text-gray-900'>
                    {getQuestionLabel(question, translation)}
                    {question.required ? ' *' : ''}
                  </label>

                  <QuestionField
                    question={question}
                    language={language}
                    translation={translation}
                    value={answers[question.id]}
                    otherValue={`${answers[`${question.id}_other`] ?? ''}`}
                    onChange={(value) => setAnswer(question.id, value)}
                    onOtherChange={(value) =>
                      setAnswer(`${question.id}_other`, value)
                    }
                  />
                </div>
              ))}

              <button
                type='submit'
                disabled={isSubmitting}
                className='rounded bg-[#004775] px-5 py-3 text-sm font-semibold text-white disabled:opacity-60'
              >
                {isSubmitting ? texts.submitting : texts.submit}
              </button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
};

export default SatisfactionSurveyPage;
